import { typeGuard } from '@/lib/utils';
import type {
  RumEvent,
  RumEventDomainContext,
  RumResourceEvent,
} from '@datadog/browser-rum';

// Not compatible with all browsers yet
// https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/deliveryType#browser_compatibility
declare global {
  interface PerformanceEntry {
    deliveryType?: string;
  }
}

function isResourceFromDiskCache(event: RumResourceEvent): boolean {
  return (
    event.resource.transfer_size === 0 &&
    event.resource.decoded_body_size !== undefined &&
    event.resource.decoded_body_size > 0
  );
}

function isRumResourceEvent(event: RumEvent): event is RumResourceEvent {
  return (
    event.type === 'resource' &&
    event.resource.type !== 'fetch' &&
    event.resource.type !== 'xhr'
  );
}

function isNextJSNavigationError(
  event: RumEvent,
  context: RumEventDomainContext
): boolean {
  if (event.type !== 'error' || !('error' in context)) return false;
  const error = context.error;
  if (
    !typeGuard<{ digest: unknown }>(error, 'digest') ||
    typeof error.digest !== 'string'
  )
    return false;

  // Next.js 16 navigation digests are control flow; production error digests
  // identify real Server Component failures and must still reach Datadog.
  const parts = error.digest.split(';');
  if (parts[0] === 'NEXT_HTTP_ERROR_FALLBACK') {
    return parts.length === 2 && ['401', '403', '404'].includes(parts[1]);
  }
  return (
    parts[0] === 'NEXT_REDIRECT' &&
    parts.length >= 5 &&
    ['push', 'replace'].includes(parts[1]) &&
    ['303', '307', '308'].includes(parts.at(-2) ?? '') &&
    parts.at(-1) === ''
  );
}

export function beforeSend(
  event: RumEvent,
  context: RumEventDomainContext
): boolean {
  if (
    isRumResourceEvent(event) &&
    event.context != null &&
    typeGuard(context, 'performanceEntry')
  ) {
    const isFromDiskCache = isResourceFromDiskCache(event);
    event.context.diskCache = isFromDiskCache;
    // Not compatible with all browsers yet
    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/deliveryType#browser_compatibility
    event.context.deliveryType =
      context.performanceEntry?.deliveryType ?? undefined;
  }
  if (isNextJSNavigationError(event, context)) {
    return false;
  }
  return true;
}
